import { range } from "@prismicio/editor-support/Array"
import { Box, Skeleton } from "@prismicio/editor-ui"
import type { ReactNode } from "react"

import {
  DocumentLayout,
  headerHeight,
  mainContentMaxWidth,
  sidePanelPadding,
  sidePanelWidth
} from "./documentLayout"

interface Props {
  screen: "new" | "existing"
}

export function DocumentSkeleton(props: Props) {
  return <DocumentLayout header={<Header />} main={<Main {...props} />} />
}

function Header() {
  return (
    <Box
      display="flex"
      flexDirection="column"
      position="fixed"
      top={0}
      height={headerHeight}
      border={{ bottom: true }}
      backgroundColor="grey2"
    >
      <Box justifyContent="center" width="100vw" padding={{ block: 16 }}>
        <Grid>
          <Box alignItems="center">
            <Skeleton width="80%" height={20} />
          </Box>
          <Box gap={8}>
            <Skeleton width={64} height={40} />
            <Skeleton width={112} height={40} />
          </Box>
          <Box gap={8} padding={{ left: sidePanelPadding("right").left }}>
            <Skeleton width={64} height={40} />
            <Skeleton width={82} height={40} />
          </Box>
        </Grid>
      </Box>
    </Box>
  )
}

function Main(props: Props) {
  const { screen } = props

  return (
    <Grid isMain>
      <SliceTableSkeleton screen={screen} />
      <ContentSkeleton screen={screen} />
      <Box
        flexDirection="column"
        padding={{ top: 24, ...sidePanelPadding("right") }}
      >
        <Skeleton height={40} />
      </Box>
    </Grid>
  )
}

interface GridProps {
  children: ReactNode
  isMain?: boolean
}

function Grid(props: GridProps) {
  const { children, isMain } = props

  return (
    <Box
      display="grid"
      position="fixed" // No body scrollbar
      maxWidth={mainContentMaxWidth}
      width="100%"
      gridTemplateColumns={`${sidePanelWidth}px 1fr ${sidePanelWidth}px`}
      top={isMain ? headerHeight : undefined}
      padding={isMain ? { top: 24 } : undefined}
    >
      {children}
    </Box>
  )
}

const existingSliceCount = 4

function SliceTableSkeleton(props: Props) {
  const { screen } = props

  return (
    <Box
      flexDirection="column"
      padding={{ ...sidePanelPadding("left"), top: 32 }}
    >
      {range(1, screen === "new" ? 1 : existingSliceCount).map((n) => (
        <Skeleton key={n} height={180} sx={{ marginBottom: 32 }} />
      ))}
    </Box>
  )
}

function ContentSkeleton(props: Props) {
  const { screen } = props

  return (
    <Box flexDirection="column">
      <Skeleton height={50} sx={{ marginBottom: 8 }} />
      <Skeleton height={240} sx={{ marginBottom: 16 }} />
      {screen === "existing" &&
        range(1, existingSliceCount).map((n) => (
          <Skeleton key={n} height={300} sx={{ marginBottom: 16 }} />
        ))}
    </Box>
  )
}

export function SlicesAndContentSkeleton() {
  return (
    <Box
      display="grid"
      width="100%"
      gridTemplateColumns={`${sidePanelWidth}px 1fr`}
      padding={{ top: 24 }}
    >
      <SliceTableSkeleton screen="existing" />
      <ContentSkeleton screen="existing" />
    </Box>
  )
}
